/**
 * ProvenanceWorkspacePanel — one reusable right-pane container that
 * renders any provenance record type (Case, Reflection, M&M, Imaging,
 * Paper, Pearl) using the existing repository lookups. All record
 * types share the same header (label + ID + close), so we do not build
 * separate layout systems per type.
 *
 * When a record cannot be resolved from the repository, the panel
 * shows a graceful "Repository record not available" state instead
 * of altering or damaging the conversation.
 */
import { useMemo } from "react";
import {
  X,
  FileText,
  Quote,
  Lightbulb,
  ClipboardList,
  Scan,
  BookOpen,
  ExternalLink,
  Sparkles,
  MessageSquare,
  Calendar,
  MapPin,
  Activity,
  Users,
  Tag,
  Link2,
  AlertCircle,
} from "lucide-react";
import {
  getCaseById,
  getReflection,
  getOperativePearl,
  getImaging,
  getMMRecord,
  getLiteratureById,
  getCollaboration,
  getTimelineEvent,
  getReflectionsByCaseId,
  getImagingByCaseId,
  getMMByCaseId,
  getOperativePearlsByCaseId,
  getPerson,
} from "@/data/repository";
import { classifyProvenanceId } from "@/context/ProvenanceWorkspaceContext";

// ---------- Type meta -----------------------------------------------
const TYPE_META = {
  case:         { label: "Case",         Icon: FileText,      accent: "amber" },
  reflection:   { label: "Reflection",   Icon: Quote,         accent: "amber" },
  pearl:        { label: "Pearl",        Icon: Lightbulb,     accent: "amber" },
  mm:           { label: "M&M",          Icon: ClipboardList, accent: "amber" },
  imaging:      { label: "Imaging",      Icon: Scan,          accent: "blue" },
  literature:   { label: "Paper",        Icon: BookOpen,      accent: "blue" },
  collaboration:{ label: "Note",         Icon: MessageSquare, accent: "amber" },
  timeline:     { label: "Timeline",     Icon: Calendar,      accent: "amber" },
  unknown:      { label: "Record",       Icon: FileText,      accent: "amber" },
};

// ---------- Repository resolver (unified) ---------------------------
function resolveRecord(type, id) {
  if (!id) return null;
  const t = type || classifyProvenanceId(id);
  try {
    switch (t) {
      case "case":        return getCaseById(id);
      case "reflection":  return getReflection(id);
      case "pearl":       return getOperativePearl(id);
      case "mm":          return getMMRecord(id);
      case "imaging":     return getImaging(id);
      case "literature":  return getLiteratureById(id);
      case "collaboration": return getCollaboration(id);
      case "timeline":    return getTimelineEvent(id);
      default:            return null;
    }
  } catch {
    return null;
  }
}

// ---------- Small view helpers --------------------------------------
function Field({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[9.5px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--nn-ink-faint))]">
        {label}
      </span>
      <span className="text-[12.5px] leading-[18px] text-[hsl(var(--nn-ink))]">
        {String(value)}
      </span>
    </div>
  );
}

function LinkChip({ id, onOpen, kind = "amber" }) {
  const type = classifyProvenanceId(id);
  const meta = TYPE_META[type] || TYPE_META.unknown;
  const isBlue = meta.accent === "blue" || kind === "blue";
  return (
    <button
      type="button"
      onClick={() => onOpen && onOpen(type, id)}
      className={"nn-prov " + (isBlue ? "nn-prov-blue" : "")}
      data-testid={`prov-workspace-link-${id}`}
      title={`Open ${meta.label} · ${id}`}
    >
      <Link2 className="h-2.5 w-2.5" strokeWidth={2} />
      <span className="uppercase tracking-[0.06em]">{meta.label}</span>
      <span className="text-[hsl(var(--nn-ink-faint))]">·</span>
      <span>{id}</span>
    </button>
  );
}

function ChipRow({ ids, onOpen }) {
  if (!ids || !ids.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {ids.filter(Boolean).map((id) => (
        <LinkChip key={id} id={id} onOpen={onOpen} />
      ))}
    </div>
  );
}

function personName(id) {
  if (!id) return null;
  const p = getPerson(id);
  return p?.fullName || p?.displayName || id;
}

// ---------- Type-specific renderers ---------------------------------
function CaseView({ rec, onOpen }) {
  const reflections = getReflectionsByCaseId(rec.caseId) || [];
  const imaging = getImagingByCaseId(rec.caseId) || [];
  const mm = getMMByCaseId(rec.caseId) || [];
  const pearls = getOperativePearlsByCaseId(rec.caseId) || [];
  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="text-[15.5px] font-semibold tracking-tight text-[hsl(var(--nn-ink))]">
          {rec.diagnosis || rec.diagnosisPrimary || rec.caseId}
        </div>
        <div className="mt-0.5 text-[11px] text-[hsl(var(--nn-ink-muted))]">
          {[rec.patientLabel, rec.age && `${rec.age}·${rec.sex || ""}`.trim(), rec.laterality].filter(Boolean).join(" · ")}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Approach" value={rec.approach || rec.approachSurgical} />
        <Field label="Resection" value={rec.extentOfResection} />
        <Field label="Koos" value={rec.koosGrade} />
        <Field label="Tumor size" value={rec.tumorSizeCm && `${rec.tumorSizeCm} cm`} />
        <Field label="POD-1 HB" value={rec.pod1HbGrade} />
        <Field label="3-wk HB" value={rec.threeWeekHbGrade} />
        <Field label="6-mo HB" value={rec.sixMonthHbGrade} />
        <Field label="Status" value={rec.currentStatus} />
      </div>
      {rec.reasonForResidual && (
        <div>
          <div className="nn-eyebrow mb-1">Why residual</div>
          <p className="text-[12.5px] leading-[18px] text-[hsl(var(--nn-ink))]">{rec.reasonForResidual}</p>
        </div>
      )}
      {reflections.length > 0 && (
        <section>
          <div className="nn-eyebrow mb-1.5">Linked reflections ({reflections.length})</div>
          <ChipRow ids={reflections.map((r) => r.reflectionId)} onOpen={onOpen} />
        </section>
      )}
      {imaging.length > 0 && (
        <section>
          <div className="nn-eyebrow mb-1.5">Linked imaging ({imaging.length})</div>
          <ChipRow ids={imaging.map((i) => i.imagingId)} onOpen={onOpen} />
        </section>
      )}
      {mm.length > 0 && (
        <section>
          <div className="nn-eyebrow mb-1.5">M&M ({mm.length})</div>
          <ChipRow ids={mm.map((m) => m.mAndMId)} onOpen={onOpen} />
        </section>
      )}
      {pearls.length > 0 && (
        <section>
          <div className="nn-eyebrow mb-1.5">Operative pearls ({pearls.length})</div>
          <ChipRow ids={pearls.map((p) => p.operativePearlId)} onOpen={onOpen} />
        </section>
      )}
    </div>
  );
}

function ReflectionView({ rec, onOpen }) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="nn-eyebrow mb-0.5">{rec.reflectionType || "Reflection"}</div>
        <p
          className="text-[13.5px] leading-[20px] italic text-[hsl(var(--nn-ink))]"
          style={{ borderLeft: "2px solid rgba(160,200,255,0.35)", paddingLeft: 10 }}
        >
          {rec.reflectionText || rec.textPreview}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Author" value={personName(rec.authorPersonId)} />
        <Field label="Captured" value={rec.dateCaptured && String(rec.dateCaptured).slice(0, 10)} />
        <Field label="Operative phase" value={rec.operativePhase} />
        <Field label="Complication-related" value={rec.complicationRelated} />
      </div>
      {rec.clinicalContext && (
        <Field label="Clinical context" value={rec.clinicalContext} />
      )}
      {rec.operativePearl && (
        <div>
          <div className="nn-eyebrow mb-1">Operative pearl</div>
          <p className="text-[12.5px] leading-[18px] text-[hsl(var(--nn-ink))]">{rec.operativePearl}</p>
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {rec.caseId && <LinkChip id={rec.caseId} onOpen={onOpen} />}
        {rec.operativePearlId && <LinkChip id={rec.operativePearlId} onOpen={onOpen} />}
      </div>
    </div>
  );
}

function PearlView({ rec, onOpen }) {
  const tags = (rec.tags || "").split(";").map((s) => s.trim()).filter(Boolean);
  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="nn-eyebrow mb-1">Operative pearl</div>
        <p className="text-[13.5px] leading-[20px] text-[hsl(var(--nn-ink))]">{rec.operativePearl || rec.pearlText}</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Phase" value={rec.operativePhase} />
        <Field label="Author" value={personName(rec.authorPersonId)} />
      </div>
      {tags.length > 0 && (
        <section>
          <div className="nn-eyebrow mb-1.5">Tags</div>
          <div className="flex flex-wrap gap-1">
            {tags.map((t) => (
              <span key={t} className="nn-chip">{t}</span>
            ))}
          </div>
        </section>
      )}
      <div className="flex flex-wrap gap-1.5">
        {rec.caseId && <LinkChip id={rec.caseId} onOpen={onOpen} />}
        {rec.reflectionId && <LinkChip id={rec.reflectionId} onOpen={onOpen} />}
      </div>
      {Array.isArray(rec.supportingCaseIds) && rec.supportingCaseIds.length > 0 && (
        <section>
          <div className="nn-eyebrow mb-1.5">Supporting cases</div>
          <ChipRow ids={rec.supportingCaseIds} onOpen={onOpen} />
        </section>
      )}
      {Array.isArray(rec.supportingImagingIds) && rec.supportingImagingIds.length > 0 && (
        <section>
          <div className="nn-eyebrow mb-1.5">Supporting imaging</div>
          <ChipRow ids={rec.supportingImagingIds} onOpen={onOpen} />
        </section>
      )}
    </div>
  );
}

function MMView({ rec, onOpen }) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="text-[15.5px] font-semibold tracking-tight text-[hsl(var(--nn-ink))]">
          {rec.complication || rec.title || rec.mAndMId}
        </div>
        <div className="mt-0.5 text-[11px] text-[hsl(var(--nn-ink-muted))]">
          {[rec.status, rec.meetingDate && String(rec.meetingDate).slice(0, 10)].filter(Boolean).join(" · ")}
        </div>
      </div>
      {rec.chronology && (
        <div>
          <div className="nn-eyebrow mb-1">Chronology</div>
          <p className="text-[12.5px] leading-[18px] text-[hsl(var(--nn-ink))]">{rec.chronology}</p>
        </div>
      )}
      {rec.keyDecisionPoint && (
        <Field label="Key decision point" value={rec.keyDecisionPoint} />
      )}
      {rec.rootCause && (
        <Field label="Root cause" value={rec.rootCause} />
      )}
      {rec.changeInPractice && (
        <Field label="Change in practice" value={rec.changeInPractice} />
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Presenter" value={personName(rec.presenterPersonId)} />
        <Field label="Meeting date" value={rec.meetingDate && String(rec.meetingDate).slice(0, 10)} />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {rec.caseId && <LinkChip id={rec.caseId} onOpen={onOpen} />}
      </div>
    </div>
  );
}

function ImagingView({ rec, onOpen }) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="text-[15px] font-semibold tracking-tight text-[hsl(var(--nn-ink))]">
          {rec.modality} · {rec.sequence}
        </div>
        <div className="mt-0.5 text-[11px] text-[hsl(var(--nn-ink-muted))]">
          {[rec.studyType, rec.studyDate && String(rec.studyDate).slice(0, 10)].filter(Boolean).join(" · ")}
        </div>
      </div>
      <div
        className="grid h-40 place-items-center overflow-hidden rounded-xl border border-white/10"
        style={{
          background:
            "radial-gradient(120% 100% at 50% 40%, rgba(160,200,255,0.14), rgba(30,40,60,0.6) 60%, rgba(14,20,34,0.9))",
        }}
      >
        <div className="flex flex-col items-center gap-1 text-[hsl(var(--nn-ink-faint))]">
          <Scan className="h-6 w-6" strokeWidth={1.4} />
          <span className="text-[10px] uppercase tracking-[0.14em]">
            {rec.filenamePlaceholder || "MRI placeholder"}
          </span>
        </div>
      </div>
      {rec.keyFinding && (
        <div>
          <div className="nn-eyebrow mb-1">Key finding</div>
          <p className="text-[12.5px] leading-[18px] text-[hsl(var(--nn-ink))]">{rec.keyFinding}</p>
        </div>
      )}
      {rec.annotationSummary && (
        <div>
          <div className="nn-eyebrow mb-1">Annotation</div>
          <p className="text-[12.5px] leading-[18px] text-[hsl(var(--nn-ink))]">{rec.annotationSummary}</p>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Koos" value={rec.koosGrade} />
        <Field label="Tumor size" value={rec.tumorSizeCm && `${rec.tumorSizeCm} cm`} />
        <Field label="Residual" value={rec.residualPresent} />
        <Field label="Annotated by" value={personName(rec.annotatedByPersonId)} />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {rec.caseId && <LinkChip id={rec.caseId} onOpen={onOpen} />}
        {rec.linkedMAndMId && <LinkChip id={rec.linkedMAndMId} onOpen={onOpen} />}
        {Array.isArray(rec.linkedReflectionIds) &&
          rec.linkedReflectionIds.slice(0, 6).map((id) => <LinkChip key={id} id={id} onOpen={onOpen} />)}
      </div>
    </div>
  );
}

function LiteratureView({ rec }) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="text-[15px] font-semibold leading-[20px] tracking-tight text-[hsl(var(--nn-ink))]">
          {rec.syntheticTitle || rec.literatureLabel}
        </div>
        <div className="mt-0.5 text-[11px] text-[hsl(var(--nn-ink-muted))]">
          {[rec.syntheticAuthors, rec.publicationYear].filter(Boolean).join(" · ")}
        </div>
      </div>
      <Field label="Topic" value={rec.topic} />
      <Field label="Study type" value={rec.studyType} />
      <Field label="Sample size" value={rec.sampleSize} />
      <Field label="Key outcome" value={rec.keyOutcome} />
      <Field label="Facial nerve outcome rate" value={rec.facialNerveOutcomeRate} />
      <Field label="Complication rate" value={rec.complicationRate} />
      {rec.keyConclusion && (
        <div>
          <div className="nn-eyebrow mb-1">Conclusion</div>
          <p className="text-[12.5px] leading-[18px] text-[hsl(var(--nn-ink))]">{rec.keyConclusion}</p>
        </div>
      )}
    </div>
  );
}

// Fallback for unrecognised or truly missing records.
function UnavailableView({ id }) {
  return (
    <div className="flex flex-col items-start gap-2 rounded-xl border border-white/[0.08] bg-white/[0.02] p-4">
      <div className="flex items-center gap-2 text-[hsl(var(--nn-warn))]">
        <AlertCircle className="h-4 w-4" strokeWidth={2} />
        <span className="text-[12px] font-medium">Repository record not available</span>
      </div>
      <p className="text-[11.5px] leading-[17px] text-[hsl(var(--nn-ink-muted))]">
        We could not resolve <span className="font-mono">{id || "this record"}</span> against the connected repository.
        This does not affect the conversation.
      </p>
    </div>
  );
}

// ---------- Root panel ---------------------------------------------
export default function ProvenanceWorkspacePanel({
  active,
  onClose,
  onOpenAnother,
  onAskAboutRecord,
  onOpenFullPage,
  isNarrow = false,
}) {
  const type = active?.type || classifyProvenanceId(active?.id);
  const meta = TYPE_META[type] || TYPE_META.unknown;
  const rec = useMemo(() => (active ? resolveRecord(type, active.id) : null), [active, type]);

  if (!active) return null;

  // Support both direct callers and re-entry via LinkChip clicks.
  const openAnother = (nextType, nextId) => {
    if (onOpenAnother) onOpenAnother(nextType, nextId);
  };

  // The "Open full record" button routes to whatever full page already
  // exists for the record type. Only surface it when a real page is
  // known — we do not fabricate new routes here.
  const fullPageHint = (() => {
    if (type === "case" && rec) return { label: "Open in Cases module", href: "/cases" };
    if (type === "mm" && rec) return { label: "Open in M&M Builder", href: "/mm" };
    return null;
  })();

  return (
    <aside
      className={
        "nn-glass nn-scroll flex flex-col overflow-y-auto rounded-2xl " +
        (isNarrow ? "h-full w-full" : "h-full w-full")
      }
      style={{ padding: "14px 14px 18px 14px" }}
      data-testid="provenance-workspace-panel"
      data-record-type={type}
      data-record-id={active.id}
      aria-label={`${meta.label} record ${active.id}`}
    >
      {/* Header */}
      <header className="mb-3 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span
            className="grid h-6 w-6 place-items-center rounded-md border"
            style={{
              background: meta.accent === "blue" ? "rgba(160,200,255,0.14)" : "rgba(255,190,120,0.14)",
              borderColor: meta.accent === "blue" ? "rgba(160,200,255,0.32)" : "rgba(255,190,120,0.32)",
              color: meta.accent === "blue" ? "hsl(var(--nn-blue))" : "hsl(var(--nn-amber))",
            }}
          >
            <meta.Icon className="h-3.5 w-3.5" strokeWidth={2} />
          </span>
          <div className="leading-tight">
            <div className="text-[10px] font-medium uppercase tracking-[0.16em] text-[hsl(var(--nn-ink-faint))]">
              {meta.label}
            </div>
            <div
              className="font-mono text-[11.5px] text-[hsl(var(--nn-ink))]"
              data-testid="provenance-workspace-id"
            >
              {active.id}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="nn-btn nn-btn-icon"
          data-testid="provenance-workspace-close"
          title="Close (Esc)"
          aria-label="Close record"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2} />
        </button>
      </header>

      {/* Body */}
      <div className="flex-1">
        {!rec ? (
          <UnavailableView id={active.id} />
        ) : type === "case" ? (
          <CaseView rec={rec} onOpen={openAnother} />
        ) : type === "reflection" ? (
          <ReflectionView rec={rec} onOpen={openAnother} />
        ) : type === "pearl" ? (
          <PearlView rec={rec} onOpen={openAnother} />
        ) : type === "mm" ? (
          <MMView rec={rec} onOpen={openAnother} />
        ) : type === "imaging" ? (
          <ImagingView rec={rec} onOpen={openAnother} />
        ) : type === "literature" ? (
          <LiteratureView rec={rec} />
        ) : (
          <UnavailableView id={active.id} />
        )}
      </div>

      {/* Actions */}
      <footer className="mt-4 flex flex-wrap items-center gap-1.5 border-t border-white/[0.06] pt-3">
        {rec && (
          <button
            type="button"
            onClick={() => onAskAboutRecord && onAskAboutRecord({ type, id: active.id, rec })}
            className="nn-btn nn-btn-primary"
            data-testid="provenance-workspace-ask"
          >
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.8} />
            Ask Ni² about this
          </button>
        )}
        {fullPageHint && (
          <button
            type="button"
            onClick={() => onOpenFullPage && onOpenFullPage(fullPageHint.href)}
            className="nn-btn nn-btn-ghost"
            data-testid="provenance-workspace-open-full"
          >
            <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.8} />
            {fullPageHint.label}
          </button>
        )}
      </footer>
    </aside>
  );
}
